import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Grid, Typography, Chip, IconButton, Avatar,
  Tooltip, LinearProgress, Stack, Skeleton, Divider,
  Alert, Button, Paper,
} from "@mui/material";
import {
  Refresh, TrendingUp, ShoppingBag,
  People, CheckCircle, Schedule, AttachMoney,
  ArrowUpward, ArrowDownward, Warning, Storefront,
  FiberManualRecord, PointOfSale, AddBox, ListAlt,
  Campaign, Inventory2, Repeat,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import {
  fetchAnalyticsOverview,
  fetchRealTimeStats,
  fetchRevenueTrend,
  fetchOrderFunnel,
} from "../../api/api";
import { useSnackbar } from "../../utils/snackbar";

// ─── Brand constants (only thing that stays hardcoded) ───────────────────────
const BRAND_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const REFRESH_INTERVAL_MS = 30_000;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const kes = (n) => `KSh ${Math.round(n || 0).toLocaleString()}`;

function nDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function pctChange(curr, prev) {
  if (!prev || prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

// ─── Sparkline (SVG, no lib) ─────────────────────────────────────────────────
function Sparkline({ data = [], color = "#667eea", height = 32, width = 72 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - Math.max(2, (v / max) * height * 0.85);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const gradId = `sg-${color.replace(/[^a-z0-9]/gi, "")}`;
  // Area fill points: close the polygon at bottom
  const areaFirst = `0,${height}`;
  const areaLast = `${width},${height}`;
  const area = `${areaFirst} ${pts} ${areaLast}`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width, height, display: "block", flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradId})`} />
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
    </svg>
  );
}

// ─── KPI Stat Card ────────────────────────────────────────────────────────────
function KPICard({ title, value, icon, iconBg, trend, trendLabel, spark, sparkColor, loading }) {
  const up = trend > 0;
  const hasTraend = trend != null && !isNaN(trend);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: "background.paper",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: (t) => t.shadows[4] },
      }}
    >
      {/* gradient accent strip */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: BRAND_GRADIENT,
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: iconBg,
          }}
        >
          {icon}
        </Avatar>

        {hasTraend && (
          <Chip
            icon={
              up ? (
                <ArrowUpward sx={{ fontSize: "11px !important" }} />
              ) : (
                <ArrowDownward sx={{ fontSize: "11px !important" }} />
              )
            }
            label={`${Math.abs(trend)}%`}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: up ? "success.main" : "error.main",
              color: "#fff",
              "& .MuiChip-icon": { color: "#fff" },
            }}
          />
        )}
      </Box>

      {loading ? (
        <>
          <Skeleton variant="text" width="55%" height={36} />
          <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
        </>
      ) : (
        <>
          <Typography
            variant="h5"
            sx={{ fontWeight: 900, lineHeight: 1.1, mb: 0.4, color: "text.primary" }}
          >
            {value ?? "—"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.3 }}>
            {title}
          </Typography>
          {trendLabel && (
            <Typography variant="caption" display="block" sx={{ color: "text.secondary", mt: 0.3, fontSize: 10 }}>
              {trendLabel}
            </Typography>
          )}
        </>
      )}

      {spark && spark.length > 1 && !loading && (
        <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
          <Sparkline data={spark} color={sparkColor} />
        </Box>
      )}
    </Paper>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            background: BRAND_GRADIENT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 16,
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Divider />
    </Box>
  );
}

// ─── Revenue bar chart (pure MUI Box) ────────────────────────────────────────
function RevenueBarChart({ days = [], loading }) {
  const [hovered, setHovered] = useState(null);
  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 120 }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, height: "100%", alignItems: "center", justifyContent: "flex-end" }}>
            <Skeleton variant="rectangular" width="100%" height={`${30 + Math.random() * 60}%`} sx={{ borderRadius: 1 }} />
          </Box>
        ))}
      </Box>
    );
  }

  if (!days.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">No revenue data available</Typography>
      </Box>
    );
  }

  const maxOnline = Math.max(...days.map((d) => d.online || 0), 1);
  const maxPos = Math.max(...days.map((d) => d.pos || 0), 1);
  const globalMax = Math.max(maxOnline, maxPos);

  return (
    <Box>
      {/* Y-axis labels */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, px: 0.5 }}>
        {[0, 25, 50, 75, 100].map((pct) => (
          <Typography key={pct} variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>
            {Math.round((pct / 100) * globalMax).toLocaleString()}
          </Typography>
        ))}
      </Box>

      {/* Bars */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: { xs: 0.5, sm: 1 },
          height: 130,
          px: 0.5,
        }}
      >
        {days.map((d, i) => {
          const onlineH = globalMax > 0 ? Math.max(3, ((d.online || 0) / globalMax) * 100) : 3;
          const posH = globalMax > 0 ? Math.max(3, ((d.pos || 0) / globalMax) * 100) : 3;
          const isHov = hovered === i;
          return (
            <Box
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                cursor: "default",
                position: "relative",
              }}
            >
              {/* Tooltip on hover */}
              {isHov && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    mb: 0.5,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.5,
                    zIndex: 10,
                    whiteSpace: "nowrap",
                    boxShadow: 3,
                  }}
                >
                  <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: "text.primary", fontSize: 10 }}>
                    {d.label}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: "primary.main", fontSize: 10 }}>
                    Online: {kes(d.online)}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: "secondary.main", fontSize: 10 }}>
                    POS: {kes(d.pos)}
                  </Typography>
                </Box>
              )}

              {/* Bar pair */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "2px",
                }}
              >
                {/* Online bar */}
                <Box
                  sx={{
                    flex: 1,
                    height: `${onlineH}%`,
                    borderRadius: "3px 3px 0 0",
                    background: isHov ? BRAND_GRADIENT : "primary.main",
                    bgcolor: isHov ? undefined : "primary.main",
                    opacity: isHov ? 1 : 0.85,
                    transition: "all 0.15s ease",
                  }}
                />
                {/* POS bar */}
                <Box
                  sx={{
                    flex: 1,
                    height: `${posH}%`,
                    borderRadius: "3px 3px 0 0",
                    bgcolor: "secondary.main",
                    opacity: isHov ? 1 : 0.7,
                    transition: "all 0.15s ease",
                  }}
                />
              </Box>

              {/* Label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9,
                  color: "text.secondary",
                  fontWeight: 600,
                  mt: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {d.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", gap: 2, mt: 1.5, justifyContent: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "primary.main" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>Online</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "secondary.main" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>POS</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Order Funnel ─────────────────────────────────────────────────────────────
function OrderFunnel({ funnel = null, loading }) {
  const steps = [
    { key: "created",   label: "Created",   color: "info.main" },
    { key: "paid",      label: "Paid",      color: "primary.main" },
    { key: "shipped",   label: "Shipped",   color: "secondary.main" },
    { key: "completed", label: "Completed", color: "success.main" },
  ];

  if (loading) {
    return (
      <Stack spacing={1.5}>
        {steps.map((s) => (
          <Box key={s.key}>
            <Skeleton variant="text" width="30%" height={14} />
            <Skeleton variant="rectangular" height={10} sx={{ borderRadius: 99, mt: 0.5 }} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (!funnel) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
        No funnel data
      </Typography>
    );
  }

  const base = funnel.created || 1;
  return (
    <Stack spacing={2}>
      {steps.map((step, i) => {
        const count = funnel[step.key] || 0;
        const pct = Math.round((count / base) * 100);
        const prevCount = i > 0 ? funnel[steps[i - 1].key] || 0 : base;
        const dropOff = i > 0 && prevCount > 0 ? Math.round(((prevCount - count) / prevCount) * 100) : null;
        return (
          <Box key={step.key}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: step.color,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {step.label}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {dropOff !== null && dropOff > 0 && (
                  <Chip
                    label={`-${dropOff}%`}
                    size="small"
                    sx={{ height: 18, fontSize: 9, bgcolor: "error.main", color: "#fff", fontWeight: 700 }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 800, color: "text.primary", minWidth: 32, textAlign: "right" }}>
                  {count.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 36, textAlign: "right" }}>
                  {pct}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 8,
                borderRadius: 99,
                bgcolor: "action.hover",
                "& .MuiLinearProgress-bar": {
                  bgcolor: step.color,
                  borderRadius: 99,
                  transition: "transform 1s ease",
                },
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

// ─── Quick Stats Card ─────────────────────────────────────────────────────────
function QuickStatItem({ label, value, color = "text.primary", loading }) {
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "background.default",
        borderRadius: 2,
        textAlign: "center",
        border: 1,
        borderColor: "divider",
      }}
    >
      {loading ? (
        <Skeleton variant="text" width="50%" height={28} sx={{ mx: "auto" }} />
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 900, color, lineHeight: 1 }}>
          {value ?? "—"}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Quick Action Button ──────────────────────────────────────────────────────
function QuickAction({ icon, label, to, gradient }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      variant="contained"
      startIcon={icon}
      fullWidth
      sx={{
        background: gradient || BRAND_GRADIENT,
        color: "#fff",
        fontWeight: 700,
        borderRadius: 2.5,
        py: 1.4,
        fontSize: 13,
        boxShadow: "none",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(102,126,234,0.35)",
          transform: "translateY(-1px)",
          background: gradient || BRAND_GRADIENT,
        },
        transition: "all 0.18s ease",
      }}
    >
      {label}
    </Button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function DashboardHome() {
  const { showAlert } = useSnackbar();

  // ── State ──────────────────────────────────────────────────────────────────
  const [rtLoading, setRtLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [revLoading, setRevLoading] = useState(true);
  const [funnelLoading, setFunnelLoading] = useState(true);

  const [rtStats, setRtStats] = useState(null);
  const [overview7d, setOverview7d] = useState(null);
  const [overview30d, setOverview30d] = useState(null);
  const [revenueDays, setRevenueDays] = useState([]);
  const [funnel, setFunnel] = useState(null);

  const [rtError, setRtError] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [revError, setRevError] = useState(null);
  const [funnelError, setFunnelError] = useState(null);

  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  // ── Fetch real-time stats ─────────────────────────────────────────────────
  const loadRealTime = useCallback(async (silent = false) => {
    if (!silent) setRtLoading(true);
    setRtError(null);
    try {
      const res = await fetchRealTimeStats();
      setRtStats(res.data?.data || res.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to load real-time stats";
      setRtError(msg);
      if (!silent) showAlert(msg, "error");
    } finally {
      if (!silent) setRtLoading(false);
    }
  }, [showAlert]);

  // ── Fetch overview ────────────────────────────────────────────────────────
  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const [r7, r30] = await Promise.all([
        fetchAnalyticsOverview("7d"),
        fetchAnalyticsOverview("30d"),
      ]);
      setOverview7d(r7.data?.data || r7.data || null);
      setOverview30d(r30.data?.data || r30.data || null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to load overview";
      setOverviewError(msg);
      showAlert(msg, "error");
    } finally {
      setOverviewLoading(false);
    }
  }, [showAlert]);

  // ── Fetch revenue trend ───────────────────────────────────────────────────
  const loadRevenueTrend = useCallback(async () => {
    setRevLoading(true);
    setRevError(null);
    try {
      const startDate = nDaysAgo(6);
      const endDate = todayISO();
      const res = await fetchRevenueTrend({ groupBy: "day", startDate, endDate });
      const raw = res.data?.data || res.data || [];
      // Normalise to array of { label, online, pos }
      const days = Array.isArray(raw)
        ? raw.map((d) => ({
            label: d.label || d.date || d._id || "",
            online: d.online ?? d.onlineRevenue ?? d.revenue ?? 0,
            pos: d.pos ?? d.posRevenue ?? 0,
          }))
        : [];
      setRevenueDays(days);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to load revenue trend";
      setRevError(msg);
      showAlert(msg, "error");
    } finally {
      setRevLoading(false);
    }
  }, [showAlert]);

  // ── Fetch order funnel ────────────────────────────────────────────────────
  const loadFunnel = useCallback(async () => {
    setFunnelLoading(true);
    setFunnelError(null);
    try {
      const res = await fetchOrderFunnel();
      const raw = res.data?.data || res.data || null;
      // Normalise: support {created, paid, shipped, completed} or array
      let normalised = null;
      if (Array.isArray(raw)) {
        normalised = raw.reduce((acc, item) => {
          if (item.status && item.count != null) acc[item.status] = item.count;
          return acc;
        }, {});
      } else if (raw && typeof raw === "object") {
        normalised = raw;
      }
      setFunnel(normalised);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to load order funnel";
      setFunnelError(msg);
      showAlert(msg, "error");
    } finally {
      setFunnelLoading(false);
    }
  }, [showAlert]);

  // ── Initial load + auto-refresh ───────────────────────────────────────────
  useEffect(() => {
    loadRealTime(false);
    loadOverview();
    loadRevenueTrend();
    loadFunnel();
  }, [loadRealTime, loadOverview, loadRevenueTrend, loadFunnel]);

  useEffect(() => {
    intervalRef.current = setInterval(() => loadRealTime(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [loadRealTime]);

  // ── Manual refresh ────────────────────────────────────────────────────────
  const handleRefreshAll = useCallback(() => {
    showAlert("Refreshing dashboard…", "info");
    loadRealTime(false);
    loadOverview();
    loadRevenueTrend();
    loadFunnel();
  }, [loadRealTime, loadOverview, loadRevenueTrend, loadFunnel, showAlert]);

  // ── Derived KPI values ────────────────────────────────────────────────────
  const revenueToday = rtStats?.revenueToday ?? overview7d?.revenueToday ?? 0;
  const ordersToday = rtStats?.ordersToday ?? overview7d?.ordersToday ?? 0;
  const activeUsers = rtStats?.activeUsers ?? rtStats?.onlineUsers ?? 0;
  const pendingOrders = rtStats?.pendingOrders ?? overview7d?.pendingOrders ?? 0;

  // Yesterday comparisons (from overview)
  const revYesterday = overview7d?.revenueYesterday ?? null;
  const ordYesterday = overview7d?.ordersYesterday ?? null;
  const revTrend = revYesterday != null ? pctChange(revenueToday, revYesterday) : null;
  const ordTrend = ordYesterday != null ? pctChange(ordersToday, ordYesterday) : null;

  // Sparklines from revenue days
  const revSparkData = revenueDays.map((d) => (d.online || 0) + (d.pos || 0));

  // 30d overview data
  const totalProducts = overview30d?.products?.total ?? overview30d?.totalProducts ?? 0;
  const activeProducts = overview30d?.products?.active ?? overview30d?.activeProducts ?? 0;
  const lowStock = overview30d?.products?.lowStock ?? overview30d?.lowStock ?? 0;
  const outOfStock = overview30d?.products?.outOfStock ?? overview30d?.outOfStock ?? 0;
  const totalCustomers = overview30d?.customers?.total ?? overview30d?.totalCustomers ?? 0;
  const newCustomers = overview30d?.customers?.newThisMonth ?? overview30d?.newCustomers ?? 0;
  const returningCustomers = overview30d?.customers?.returning ?? overview30d?.returningCustomers ?? 0;

  // Shared loading flags
  const anyLoading = rtLoading || overviewLoading || revLoading || funnelLoading;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100%", p: { xs: 2, md: 2.5 } }}>

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                color: "text.primary",
                letterSpacing: -0.5,
                background: BRAND_GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Command Centre
            </Typography>
            {/* Live indicator */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.2,
                py: 0.3,
                bgcolor: "success.main",
                borderRadius: 99,
              }}
            >
              <FiberManualRecord
                sx={{
                  fontSize: 8,
                  color: "#fff",
                  animation: "dashLivePulse 2s infinite",
                }}
              />
              <Typography sx={{ fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: 1 }}>
                LIVE
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {new Date().toLocaleDateString("en-KE", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            {lastUpdated &&
              ` · Last updated ${lastUpdated.toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" })}`}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {anyLoading && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LinearProgress
                sx={{
                  width: 80,
                  borderRadius: 99,
                  height: 3,
                  "& .MuiLinearProgress-bar": {
                    background: BRAND_GRADIENT,
                  },
                }}
              />
            </Box>
          )}
          <Tooltip title="Refresh all data">
            <IconButton
              onClick={handleRefreshAll}
              disabled={anyLoading}
              size="small"
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── ERROR BANNERS ────────────────────────────────────────────────── */}
      {(rtError || overviewError || revError || funnelError) && (
        <Box sx={{ mb: 2.5 }}>
          {rtError && (
            <Alert severity="warning" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setRtError(null)}>
              Real-time stats: {rtError}
            </Alert>
          )}
          {overviewError && (
            <Alert severity="warning" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setOverviewError(null)}>
              Overview: {overviewError}
            </Alert>
          )}
          {revError && (
            <Alert severity="warning" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setRevError(null)}>
              Revenue trend: {revError}
            </Alert>
          )}
          {funnelError && (
            <Alert severity="warning" sx={{ borderRadius: 2 }} onClose={() => setFunnelError(null)}>
              Order funnel: {funnelError}
            </Alert>
          )}
        </Box>
      )}

      {/* ── 1. KPI ROW ──────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          {
            title: "Revenue Today",
            value: kes(revenueToday),
            icon: <AttachMoney sx={{ color: "#22c55e", fontSize: 22 }} />,
            iconBg: "rgba(34,197,94,0.12)",
            trend: revTrend,
            trendLabel: revTrend != null ? "vs yesterday" : undefined,
            spark: revSparkData,
            sparkColor: "#22c55e",
            loading: rtLoading || overviewLoading,
          },
          {
            title: "Orders Today",
            value: ordersToday.toLocaleString(),
            icon: <ShoppingBag sx={{ color: "#667eea", fontSize: 22 }} />,
            iconBg: "rgba(102,126,234,0.12)",
            trend: ordTrend,
            trendLabel: ordTrend != null ? "vs yesterday" : undefined,
            spark: revSparkData.map((v, i) => overview7d?.dailyOrders?.[i] ?? v),
            sparkColor: "#667eea",
            loading: rtLoading || overviewLoading,
          },
          {
            title: "Active Users",
            value: activeUsers.toLocaleString(),
            icon: <People sx={{ color: "#3b82f6", fontSize: 22 }} />,
            iconBg: "rgba(59,130,246,0.12)",
            trend: null,
            spark: [],
            sparkColor: "#3b82f6",
            loading: rtLoading,
          },
          {
            title: "Pending Orders",
            value: pendingOrders.toLocaleString(),
            icon: <Schedule sx={{ color: "#f59e0b", fontSize: 22 }} />,
            iconBg: "rgba(245,158,11,0.12)",
            trend: null,
            spark: [],
            sparkColor: "#f59e0b",
            loading: rtLoading || overviewLoading,
          },
        ].map((kpi, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <KPICard {...kpi} />
          </Grid>
        ))}
      </Grid>

      {/* ── 2. REVENUE CHART + ORDER FUNNEL ─────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Revenue chart */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <SectionHeader
              icon={<TrendingUp sx={{ fontSize: 16 }} />}
              title="Revenue — Last 7 Days"
              subtitle="Online vs POS"
            />
            {revError && !revenueDays.length ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>Could not load revenue chart.</Alert>
            ) : (
              <RevenueBarChart days={revenueDays} loading={revLoading} />
            )}
          </Paper>
        </Grid>

        {/* Order funnel */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <SectionHeader
              icon={<CheckCircle sx={{ fontSize: 16 }} />}
              title="Order Funnel"
              subtitle="Current period"
            />
            {funnelError && !funnel ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>Could not load funnel data.</Alert>
            ) : (
              <OrderFunnel funnel={funnel} loading={funnelLoading} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── 3. QUICK STATS GRID ──────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Products */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper", height: "100%" }}>
            <SectionHeader
              icon={<Inventory2 sx={{ fontSize: 16 }} />}
              title="Products"
              subtitle="30-day snapshot"
            />
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <QuickStatItem label="Total" value={totalProducts.toLocaleString()} loading={overviewLoading} />
              </Grid>
              <Grid item xs={6}>
                <QuickStatItem label="Active" value={activeProducts.toLocaleString()} color="success.main" loading={overviewLoading} />
              </Grid>
              <Grid item xs={6}>
                <QuickStatItem label="Low Stock" value={lowStock.toLocaleString()} color="warning.main" loading={overviewLoading} />
              </Grid>
              <Grid item xs={6}>
                <QuickStatItem label="Out of Stock" value={outOfStock.toLocaleString()} color="error.main" loading={overviewLoading} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customers */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper", height: "100%" }}>
            <SectionHeader
              icon={<People sx={{ fontSize: 16 }} />}
              title="Customers"
              subtitle="30-day snapshot"
            />
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <QuickStatItem label="Total" value={totalCustomers.toLocaleString()} loading={overviewLoading} />
              </Grid>
              <Grid item xs={6}>
                <QuickStatItem label="New This Month" value={newCustomers.toLocaleString()} color="primary.main" loading={overviewLoading} />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Repeat sx={{ color: "secondary.main", fontSize: 20 }} />
                  <Box>
                    {overviewLoading ? (
                      <Skeleton variant="text" width={60} height={22} />
                    ) : (
                      <Typography variant="h6" sx={{ fontWeight: 900, color: "secondary.main", lineHeight: 1 }}>
                        {returningCustomers.toLocaleString()}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10, fontWeight: 600 }}>
                      Returning customers
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Summary metrics */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper", height: "100%" }}>
            <SectionHeader
              icon={<Storefront sx={{ fontSize: 16 }} />}
              title="Store Health"
              subtitle="7-day"
            />
            <Stack spacing={1.5}>
              {[
                {
                  label: "Avg Order Value",
                  value: kes(overview7d?.avgOrderValue ?? 0),
                  color: "primary.main",
                  icon: <AttachMoney sx={{ fontSize: 18, color: "primary.main" }} />,
                },
                {
                  label: "Completion Rate",
                  value: `${overview7d?.completionRate ?? 0}%`,
                  color: "success.main",
                  icon: <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />,
                },
                {
                  label: "Cancellation Rate",
                  value: `${overview7d?.cancellationRate ?? 0}%`,
                  color: "error.main",
                  icon: <Warning sx={{ fontSize: 18, color: "error.main" }} />,
                },
              ].map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1.2,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {row.icon}
                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12 }}>
                      {row.label}
                    </Typography>
                  </Box>
                  {overviewLoading ? (
                    <Skeleton variant="text" width={60} height={20} />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 800, color: row.color }}>
                      {row.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* ── 4. QUICK ACTIONS ─────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper", mb: 2.5 }}>
        <SectionHeader
          icon={<PointOfSale sx={{ fontSize: 16 }} />}
          title="Quick Actions"
        />
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <QuickAction
              to="/admin/pos"
              icon={<PointOfSale />}
              label="Open POS"
              gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <QuickAction
              to="/admin/products"
              icon={<AddBox />}
              label="Add Product"
              gradient={BRAND_GRADIENT}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <QuickAction
              to="/admin/orders"
              icon={<ListAlt />}
              label="View Orders"
              gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <QuickAction
              to="/admin/campaigns"
              icon={<Campaign />}
              label="Run Campaign"
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* pulse animation */}
      <style>{`
        @keyframes dashLivePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </Box>
  );
}
